import React from 'react';
import { Lightbulb, TrendingUp } from 'lucide-react';
import type { CategorySectionData } from '../../types';

interface ResultsCategoriesProps {
  categorySection: CategorySectionData;
}

const getCardBackground = (index: number) => (index % 2 === 0 ? 'bg-white' : 'bg-gray-50');

const getBarColor = (index: number) => {
  const colors = ['bg-blue-200', 'bg-green-200', 'bg-orange-200', 'bg-purple-200', 'bg-yellow-200'];
  return colors[index % colors.length];
};

const getTextColor = (index: number) => {
  const colors = ['text-blue-600', 'text-green-600', 'text-orange-600', 'text-purple-600', 'text-yellow-600'];
  return colors[index % colors.length];
};

const getPalette = (index: number) => {
  const colors = [
    { bg: 'bg-blue-50', text: 'text-blue-600' },
    { bg: 'bg-green-50', text: 'text-green-600' },
    { bg: 'bg-orange-50', text: 'text-orange-600' },
    { bg: 'bg-purple-50', text: 'text-purple-600' },
    { bg: 'bg-yellow-50', text: 'text-yellow-600' },
  ];
  return colors[index % colors.length];
};

const ResultsCategories: React.FC<ResultsCategoriesProps> = ({ categorySection }) => {
  const categories = (categorySection['category-itens'] || []).filter(
    (category) => category['category-item-insight'] !== null && category['category-item-title'] !== null
  );

  return (
    <div className="w-full p-8 mt-16 rounded-lg shadow-lg bg-white">
      <div className="bg-white text-gray-700 py-2 text-start">
        <h2 className="text-2xl font-bold mb-2">{categorySection['category-title']}</h2>
      </div>

      <div className="space-y-16">
        {categories.map((category, index) => {
          const colors = getPalette(index);

          return (
            <div key={index} className={`mb-8 last:mb-0 p-6 rounded-lg shadow-md ${getCardBackground(index)}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="items-start">
                  <h3 className="text-xl font-semibold text-gray-700">{category['category-item-title']}</h3>
                  <p className="text-gray-500 text-sm">{category['category-item-level']}</p>
                </div>
                <div className="text-right">
                  <div className={`text-lg font-bold ${getTextColor(index)}`}>
                    {category['category-item-score']}/12
                  </div>
                  <div className="text-sm text-gray-500">{category['category-item-percent']}%</div>
                </div>
              </div>

              <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                <div
                  className={`h-2 rounded-full ${getBarColor(index)}`}
                  style={{ width: `${category['category-item-percent']}%` }}
                />
              </div>

              {category['category-item-advice'] && (
                <div className="mb-6">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <div className={`w-8 h-8 ${colors.bg} rounded-full flex items-center justify-center`}>
                        <TrendingUp className={`w-4 h-4 ${colors.text}`} />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-800 mb-2">Insight:</h4>
                      <p className="text-gray-700 text-sm">{category['category-item-insight']}</p>
                    </div>
                  </div>
                </div>
              )}

              {category['category-item-insight'] && (
                <div className="mb-6">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <div className={`w-8 h-8 ${colors.bg} rounded-full flex items-center justify-center`}>
                        <Lightbulb className={`w-4 h-4 ${colors.text}`} />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-800 mb-2">Dica prática:</h4>
                      <p className="text-gray-700 text-sm">{category['category-item-advice']}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ResultsCategories;
